import { conditionalArrayItem } from 'BMapsSrc/util/js_utils';
import {
    BackgroundColors, DefaultBackgroundColor, ColorStyles, DefaultColorStyle,
} from '../../themes';
import { DefaultBindingSiteRadius } from '../../utils';

export const PREFS_TYPE = {
    boolean: 'boolean',
    enum: 'enum',
    int: 'int',
};

export const preferences = [
    {
        id: 'ShowCommandData',
        label: 'Show Command Data',
        type: PREFS_TYPE.boolean,
        default: false,
        availability: 'lab',
    },
    {
        id: 'DontPromptBeforeExit',
        label: "Don't Prompt Before Exit",
        type: PREFS_TYPE.boolean,
        default: false,
        availability: 'lab',
    },
    {
        id: 'MinimizationEngine',
        label: 'Minimization Engine',
        type: PREFS_TYPE.enum,
        // 'Local' routes the interactive MM minimization path to the in-browser
        // engine. MM and BFD remain the server-backed options. Default is MM.
        values: [
            { id: 'MM', label: 'OpenMM' },
            { id: 'BFD', label: 'Legacy Bioleap Energies' },
            { id: 'Local', label: 'In-Browser (experimental)', availability: 'lab' },
        ],
        default: 'MM',
    },
    {
        id: 'EnergyEngine',
        label: 'Energy Calculation Engine',
        type: PREFS_TYPE.enum,
        values: [{ id: 'MM', label: 'OpenMM' }, { id: 'BFD', label: 'Legacy Bioleap Energies' }],
        default: 'MM',
    },
    {
        id: 'Forcefield',
        label: 'Forcefield',
        type: PREFS_TYPE.enum,
        values: [{ id: 'Amber', label: 'Amber' }, { id: 'OpenFF', label: 'OpenFF' }],
        default: 'Amber',
        availability: 'lab',
    },
    {
        id: 'FragmentLoading',
        label: 'Fragment loading strategy',
        type: PREFS_TYPE.enum,
        values: [
            { id: 'greedy', label: 'Load default fragments with protein' },
            { id: 'lazy', label: 'Manually load fragments with Fragment Manager' },
        ],
        default: 'greedy',
    },
    {
        id: 'MaxMolecules',
        label: 'Max. molecules to load from SDF',
        type: PREFS_TYPE.int,
        default: 100,
    },
    {
        id: 'UseAlignment',
        label: 'Align selectivity proteins to first loaded protein',
        type: PREFS_TYPE.boolean,
        default: true,
    },
    {
        id: 'ColorScheme',
        label: 'Color Scheme',
        type: PREFS_TYPE.enum,
        values: Object.keys(BackgroundColors).map((v) => ({ id: v, label: v })),
        default: DefaultBackgroundColor,
    },
    {
        // TODO: consider introducing a float preference type and a "step" option
        // Binding site radius steps by 0.5 with the mousewheel
        id: 'BindingSiteDistance',
        label: 'Binding Site Distance (Å)',
        type: PREFS_TYPE.int,
        default: DefaultBindingSiteRadius,
    },
    {
        id: 'UseAutoGiFE',
        label: 'Request GiFE energies automatically',
        type: PREFS_TYPE.boolean,
        default: false,
        availability: 'lab',
    },
    {
        id: 'UseAutoExternalProperties',
        label: 'Fetch external properties when loading compounds',
        type: PREFS_TYPE.boolean,
        default: true,
    },
    {
        id: 'PiPiAlgorithm',
        label: 'Pi-Pi Identification Algorithm',
        type: PREFS_TYPE.enum,
        values: [{ id: 'bmaps', label: 'BMaps' }, { id: 'prolif', label: 'ProLIF LID' }],
        default: 'prolif',
    },
    {
        id: 'ProteinPdbMethod',
        label: 'Protein PDB Method',
        type: PREFS_TYPE.enum,
        values: [{ id: 'local', label: 'Local BMaps' }, { id: 'server', label: 'BMaps Server' }],
        default: 'local',
        availability: 'lab',
    },
    {
        id: 'UseMockServiceWorker',
        label: 'Use Mock Service Worker',
        type: PREFS_TYPE.boolean,
        default: false,
        availability: 'dev',
    },
    {
        // The 3D-viewer backend, switched live (display_mgr). The page starts with the stored
        // choice (visualizerChoice.js); only backends in Loader.CanvasVisualizers are offered,
        // one that cannot run here (isSupported()) is shown disabled. If the chosen backend
        // fails to load, the app falls back to 3Dmol. Label wording: owner decisions 2026-09-23
        // ("3Dmol", not "3Dmol.js"; a lowercase m everywhere, 2026-09-28) and 2026-09-28
        // ("Includes PyMOL\u2122": the PyMOL licence's
        // trademark clause allows "Includes PyMOL\u2122" but not naming the viewer "PyMOL"; was
        // "WASM PyMOL"). `noun` names it in sentences (notices, the unavailable note); `footnote`
        // shows under the field while it is listed (PyMOLCommands.TrademarkNotice, same text).
        // The ids are what is stored.
        id: 'Visualizer',
        label: '3D Viewer',
        type: PREFS_TYPE.enum,
        values: [
            { id: '3dmol', label: '3Dmol' },
            {
                id: 'pymol',
                label: 'Includes PyMOL\u2122',
                noun: 'The viewer that includes PyMOL\u2122',
                footnote: 'PyMOL is a trademark of Schrodinger, LLC.',
            },
        ],
        default: '3dmol',
        availability: 'dev',
        // Not `?Visualizer=` like other preferences (BrowserApp.applyAppOptions): that would
        // persist the sender's choice in the recipient's browser and rebuild the scene after
        // load. The URL selector is `?visualizer=` (visualizerChoice.js), for this page only.
        urlOverride: false,
    },
    {
        // How protein ribbons (cartoons) are drawn: PyMOL's own proportions, or the ones of
        // Schrödinger's Maestro (thinner, narrower ribbons and loops, grey helix interiors -
        // the numbers Schrödinger's own Maestro->PyMOL export uses). Applied live through
        // the visualizer seam (SetCartoonStyle); each backend maps the name to what it can do.
        // Named for the look, not the vendor (owner decision O11, 2026-09-26): 'maestro' is
        // labelled "BMaps" because BMaps' 3Dmol fork already draws those ribbon widths - so on
        // 3Dmol the preference changes nothing: both options are BMaps' usual look there.
        id: 'CartoonStyle',
        label: 'Cartoon style',
        type: PREFS_TYPE.enum,
        values: [
            { id: 'pymol', label: 'PyMOL' },
            { id: 'maestro', label: 'BMaps' },
        ],
        default: 'pymol',
        availability: 'dev',
    },
    {
        // How atoms and ribbons are coloured by default: BMaps' way (carbons by chain or
        // compound, ribbons by chain) or Schrödinger Maestro's (every carbon light grey -
        // "Element (Light Carbons)" - and ribbons by secondary structure: red helices, cyan
        // strands, grey loops). Applied live through the visualizer seam (SetColorStyle) in
        // each backend's default-colour path, so a custom colour, selectivity colouring or a
        // selection still wins. Named for the look, not the vendor (owner decision
        // 2026-09-26, with O11); "Color style" to match the id and the app's spelling.
        id: 'ColorStyle',
        label: 'Color style',
        type: PREFS_TYPE.enum,
        values: [
            { id: ColorStyles.bmaps, label: 'BMaps (chains and compounds)' },
            { id: ColorStyles.maestro, label: 'Light carbons, ribbons by secondary structure' },
        ],
        default: DefaultColorStyle,
        availability: 'dev',
    },
];
